/**
 * Every lead in your own Google Contacts — the Android half of ADR 0108.
 *
 * The screen reads two rows and calls two things. The rows: your own Google
 * connection (is it there, and does it allow contacts), and your sync row
 * (switched on, and what the last run did). The calls: the database function
 * that flips your switch, and the Edge Function that runs the sync now rather
 * than waiting for the next scheduled run. ADR 0128.
 */

import { GOOGLE_CONTACTS_SCOPE } from "@roof-hawk/core";

import { supabase } from "./supabase";

export interface GoogleContactRunResult {
  added: number;
  joined: number;
  updated: number;
  unchanged: number;
  gone: number;
  skipped: { name: string; reason: string }[];
  sharedNumbers: { key: string; names: string[] }[];
  unfinished?: string;
}

export interface GoogleContactSyncState {
  /** Your Google account is connected at all. */
  connected: boolean;
  /** The address it is connected as, which is the account the phone reads. */
  accountEmail: string | null;
  /** That connection was made with the contacts permission. */
  allowsContacts: boolean;
  enabled: boolean;
  lastRunAt: string | null;
  lastResult: GoogleContactRunResult | null;
  lastError: string | null;
}

export async function fetchGoogleContactSync(
  profileId: string,
): Promise<GoogleContactSyncState> {
  const [account, sync] = await Promise.all([
    supabase
      .from("gmail_accounts")
      .select("email_address, scope")
      .eq("profile_id", profileId)
      .maybeSingle(),
    supabase
      .from("google_contact_sync")
      .select("sync_enabled, last_run_at, last_result, last_error")
      .eq("profile_id", profileId)
      .maybeSingle(),
  ]);
  if (account.error) throw new Error(account.error.message);
  if (sync.error) throw new Error(sync.error.message);

  const a = account.data as { email_address: string; scope: string } | null;
  const s = sync.data as {
    sync_enabled: boolean;
    last_run_at: string | null;
    last_result: GoogleContactRunResult | null;
    last_error: string | null;
  } | null;

  return {
    connected: a !== null,
    accountEmail: a?.email_address ?? null,
    allowsContacts: Boolean(a?.scope?.includes(GOOGLE_CONTACTS_SCOPE)),
    enabled: s?.sync_enabled === true,
    lastRunAt: s?.last_run_at ?? null,
    lastResult: s?.last_result ?? null,
    lastError: s?.last_error ?? null,
  };
}

/** Flip your own switch, and return where it actually landed. */
export async function setGoogleContactSync(enabled: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc("set_google_contact_sync", {
    p_enabled: enabled,
  });
  if (error) throw new Error(error.message);
  return data as boolean;
}

/**
 * Run your sync now. Throws the sentence the function answered with, so the
 * screen shows why rather than a status code.
 */
export async function runGoogleContactSync(): Promise<void> {
  const { data, error } = await supabase.functions.invoke(
    "google-contacts-sync",
    { body: { action: "run" } },
  );
  if (error) throw new Error(error.message);
  const answer = data as { ok?: boolean; error?: string } | null;
  if (answer?.ok === false) throw new Error(answer.error ?? "The sync failed.");
}
